import { createContext, useContext } from "react";

import { useWindowDimensions } from "@luce/core/hooks";

import { BottomSheet } from "../../molecules/bottom-sheet";
import { Dialog } from "../../molecules/dialog";
import type {
  AdaptiveModalBodyProps,
  AdaptiveModalCloseProps,
  AdaptiveModalDescriptionProps,
  AdaptiveModalFooterProps,
  AdaptiveModalHeaderProps,
  AdaptiveModalRootProps,
  AdaptiveModalTitleProps,
} from "./types";

/** Which underlying surface the modal resolves to for the current viewport. */
type AdaptiveSurface = "dialog" | "sheet";

/**
 * Resolved surface, published by `Root` so each compound part forwards to the
 * matching `Dialog.*` / `BottomSheet.*` part without prop drilling.
 */
const SurfaceContext = createContext<AdaptiveSurface | null>(null);

const useSurface = (): AdaptiveSurface => {
  const surface = useContext(SurfaceContext);
  if (!surface) {
    throw new Error("AdaptiveModal compound parts must be rendered inside <AdaptiveModal.Root>");
  }
  return surface;
};

/**
 * AdaptiveModal.Root
 *
 * Resolves the surface from the viewport — `BottomSheet` on mobile (and always
 * on native), `Dialog` on tablet/desktop — and forwards props to it. The switch
 * is invisible to assistive tech: both surfaces announce as modal dialogs.
 *
 * A viewport resize that crosses the breakpoint while a modal is open remounts
 * the surface — Dialog and BottomSheet are separate primitives with no shared
 * instance, so detent position, Body scroll, and focus are lost and the swap is
 * not animated. This is the documented DS-046 edge case; it is rare on web and
 * never happens on native (always BottomSheet).
 */
const Root = ({
  open,
  onOpenChange,
  dismissible,
  overlay,
  dialogSize,
  bottomSheetDetents,
  showHandle,
  handleOnly,
  className,
  testID,
  children,
}: AdaptiveModalRootProps) => {
  const { isMobile } = useWindowDimensions();
  const surface: AdaptiveSurface = isMobile ? "sheet" : "dialog";

  // The surface context wraps `children` (not the whole surface) so it crosses
  // the native portal: Dialog/BottomSheet re-root their content to a PortalHost,
  // which would otherwise leave the compound parts without a resolved surface.
  const surfaceChildren = (
    <SurfaceContext.Provider value={surface}>{children}</SurfaceContext.Provider>
  );

  return surface === "sheet" ? (
    <BottomSheet.Root
      open={open}
      onOpenChange={onOpenChange}
      dismissible={dismissible}
      overlay={overlay}
      detents={bottomSheetDetents}
      showHandle={showHandle}
      handleOnly={handleOnly}
      className={className}
      testID={testID}
    >
      {surfaceChildren}
    </BottomSheet.Root>
  ) : (
    <Dialog.Root
      open={open}
      onOpenChange={onOpenChange}
      dismissible={dismissible}
      overlay={overlay}
      size={dialogSize}
      className={className}
      testID={testID}
    >
      {surfaceChildren}
    </Dialog.Root>
  );
};

const Header = ({ children, align, className }: AdaptiveModalHeaderProps) => {
  const surface = useSurface();
  return surface === "sheet" ? (
    <BottomSheet.Header align={align} className={className}>
      {children}
    </BottomSheet.Header>
  ) : (
    <Dialog.Header align={align} className={className}>
      {children}
    </Dialog.Header>
  );
};

const Title = ({ children, className }: AdaptiveModalTitleProps) => {
  const surface = useSurface();
  return surface === "sheet" ? (
    <BottomSheet.Title className={className}>{children}</BottomSheet.Title>
  ) : (
    <Dialog.Title className={className}>{children}</Dialog.Title>
  );
};

const Description = ({ children, className }: AdaptiveModalDescriptionProps) => {
  const surface = useSurface();
  return surface === "sheet" ? (
    <BottomSheet.Description className={className}>{children}</BottomSheet.Description>
  ) : (
    <Dialog.Description className={className}>{children}</Dialog.Description>
  );
};

const Body = ({ children, className }: AdaptiveModalBodyProps) => {
  const surface = useSurface();
  return surface === "sheet" ? (
    <BottomSheet.Body className={className}>{children}</BottomSheet.Body>
  ) : (
    <Dialog.Body className={className}>{children}</Dialog.Body>
  );
};

const Footer = ({ children, layout, className }: AdaptiveModalFooterProps) => {
  const surface = useSurface();
  return surface === "sheet" ? (
    <BottomSheet.Footer layout={layout} className={className}>
      {children}
    </BottomSheet.Footer>
  ) : (
    <Dialog.Footer layout={layout} className={className}>
      {children}
    </Dialog.Footer>
  );
};

/**
 * AdaptiveModal.Close
 *
 * Forwards to the active surface's close part. With no `children` each surface
 * keeps its own default — `Dialog.Close` renders its corner icon button,
 * `BottomSheet.Close` renders nothing — so pass an explicit child when a
 * consistent control is wanted across both surfaces.
 */
const Close = ({ asChild, children, disabled }: AdaptiveModalCloseProps) => {
  const surface = useSurface();
  return surface === "sheet" ? (
    <BottomSheet.Close asChild={asChild} disabled={disabled}>
      {children}
    </BottomSheet.Close>
  ) : (
    <Dialog.Close asChild={asChild} disabled={disabled}>
      {children}
    </Dialog.Close>
  );
};

Root.displayName = "AdaptiveModal.Root";
Header.displayName = "AdaptiveModal.Header";
Title.displayName = "AdaptiveModal.Title";
Description.displayName = "AdaptiveModal.Description";
Body.displayName = "AdaptiveModal.Body";
Footer.displayName = "AdaptiveModal.Footer";
Close.displayName = "AdaptiveModal.Close";

// Parts are exported individually so this stays a components-only module for
// React Fast Refresh (mirrors the BottomSheet molecule). `index.ts` assembles
// them into the `AdaptiveModal` compound object.
export { Root, Header, Title, Description, Body, Footer, Close };
